import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, useLocation } from "wouter";
import {
  useGetSession,
  getGetSessionQueryKey,
  useGetSessionQuestions,
  getGetSessionQuestionsQueryKey,
  useListSessionAnswers,
  getListSessionAnswersQueryKey,
  useSubmitAnswer,
  useUpdateSession,
} from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  PlaneTakeoff, CheckCircle2, XCircle, ChevronRight,
  CheckCircle, LogOut, Bookmark, BookmarkCheck, SkipForward, GraduationCap, Flag
} from "lucide-react";
import { ZoomableImage } from "@/components/zoomable-image";
import { cn } from "@/lib/utils";

import { useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "@/contexts/language";
import { useToast } from "@/hooks/use-toast";
import { fetchWithAuth } from "@/lib/api";
import { shuffleMcqOptionIndices } from "@/lib/shuffleMcqOptionIndices";
import { useSubscription } from "@/contexts/subscription";
import type { LicenseTrack } from "@/contexts/track";

function licenseTrackFromSession(s: { licenseTrack?: string | null } | undefined | null): LicenseTrack {
  const t = s?.licenseTrack;
  if (t === "ppl" || t === "cpl" || t === "pstar" || t === "roca") return t;
  return "cpl";
}

const BACKEND_URL = (import.meta.env.VITE_BACKEND_URL as string | undefined)?.replace(/\/$/, "") ?? "";

const BOOKMARK_KEY = "cpl_saved_questions";

function getOrCreateDeviceId(): string {
  let id = localStorage.getItem("cpl_device_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("cpl_device_id", id);
  }
  return id;
}

const TAB_INSTANCE_KEY = "cpl_test_tab_instance";

/** One id per browser tab/window (not shared across tabs like localStorage). */
function getOrCreateTabInstanceId(): string {
  try {
    let id = sessionStorage.getItem(TAB_INSTANCE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(TAB_INSTANCE_KEY, id);
    }
    return id;
  } catch {
    return getOrCreateDeviceId();
  }
}

function loadBookmarks(): Set<number> {
  try {
    const raw = localStorage.getItem(BOOKMARK_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch (_err) {
    return new Set();
  }
}

function saveBookmarks(set: Set<number>) {
  localStorage.setItem(BOOKMARK_KEY, JSON.stringify([...set]));
}

export default function Session() {
  const { id } = useParams<{ id: string }>();
  const sessionId = parseInt(id || "0", 10);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();
  const s = t.session;
  const { toast } = useToast();
  const { getPlanForTrack } = useSubscription();

  // ── Data fetching (must be declared before any hook that reads them) ──────
  const { data: session, isLoading: sessionLoading } = useGetSession(sessionId, {
    query: { enabled: !!sessionId, queryKey: getGetSessionQueryKey(sessionId) }
  });

  const plan = getPlanForTrack(licenseTrackFromSession(session));

  const { data: questions, isLoading: questionsLoading } = useGetSessionQuestions(sessionId, {
    query: { enabled: !!sessionId, queryKey: getGetSessionQuestionsQueryKey(sessionId) }
  });
  const { data: priorAnswers, isLoading: answersLoading } = useListSessionAnswers(sessionId, {
    query: { enabled: !!sessionId, queryKey: getListSessionAnswersQueryKey(sessionId) }
  });

  const submitAnswer = useSubmitAnswer();
  const updateSession = useUpdateSession();

  // ── Single-device test session enforcement ───────────────────────────────
  const [testBlocked, setTestBlocked] = useState(false);
  const [testBlockMessage, setTestBlockMessage] = useState("");
  const [testSessionId, setTestSessionId] = useState<string | null>(null);
  // Ref mirrors testSessionId so the keepalive cleanup can always read the latest value
  const testSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!session || session.completedAt) return;

    const deviceId = getOrCreateDeviceId();
    const clientInstanceId = getOrCreateTabInstanceId();
    const mode = ["weakness", "saved"].includes(session.mode ?? "") ? "turbulence" : "training";

    const token = localStorage.getItem("token");
    fetch(`${BACKEND_URL}/api/test/start`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ deviceId, mode, clientInstanceId }),
    })
      .then(async (res) => {
        if (res.status === 409) {
          const body = await res.json().catch(() => ({}));
          setTestBlocked(true);
          setTestBlockMessage(
            "A test is already in progress in another tab, window, or device. Close it or finish that session first.",
          );
          return;
        }
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        if (data?.testSessionId) {
          testSessionIdRef.current = data.testSessionId;
          setTestSessionId(data.testSessionId);
        }
      })
      .catch(() => {});

    return () => {
      const tsId = testSessionIdRef.current;
      if (!tsId) return;
      const token = localStorage.getItem("token");
      fetch(`${BACKEND_URL}/api/test/submit`, {
        method: "POST",
        keepalive: true,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ testSessionId: tsId }),
      }).catch(() => {});
    };
  }, [session?.mode, session?.completedAt, sessionId]);

  // Heartbeat — runs every 25s once testSessionId is set via state (triggering this effect)
  useEffect(() => {
    if (!testSessionId || testBlocked) return;

    const deviceId = getOrCreateDeviceId();
    const clientInstanceId = getOrCreateTabInstanceId();

    const ping = () => {
      const token = localStorage.getItem("token");
      fetch(`${BACKEND_URL}/api/test/heartbeat`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ testSessionId, deviceId, clientInstanceId }),
      })
        .then(async (res) => {
          if (res.status === 409 || res.status === 410) {
            const body = await res.json().catch(() => ({}));
            setTestBlocked(true);
            setTestBlockMessage(
              res.status === 410
                ? "This test session has ended or was replaced."
                : "A test is already in progress in another tab, window, or device. Close it or finish that session first.",
            );
          }
        })
        .catch(() => {});
    };

    ping();
    const interval = setInterval(ping, 25_000);

    return () => clearInterval(interval);
  }, [testSessionId, testBlocked]);

  // Save active session to localStorage so practice page can offer resume
  useEffect(() => {
    if (!sessionId) return;
    localStorage.setItem("cpl_active_session", String(sessionId));
    return () => {
      // Only clear when unmounting — completion clears it explicitly below
    };
  }, [sessionId]);

  const shuffledQuestions = useMemo(() => {
    if (!questions) return [];
    // In FR mode only include questions that have a full French translation
    const eligible = language === "fr"
      ? questions.filter(q => q.textFr && Array.isArray(q.optionsFr) && q.optionsFr.length === q.options.length && q.explanationFr)
      : questions;
    if (plan === "free") {
      return eligible.map(q => ({
        ...q,
        originalIndices: [0, 1, 2, 3].slice(0, q.options.length),
      }));
    }
    return eligible.map(q => {
      const indices = shuffleMcqOptionIndices(q.options.length, q.options, q.optionsFr ?? null);
      const shuffledOptions = indices.map(i => q.options[i]);
      const correctOrig = Math.trunc(Number(q.correctOptionIndex));
      const co = Number.isFinite(correctOrig)
        ? Math.min(Math.max(correctOrig, 0), q.options.length - 1)
        : 0;
      const newCorrectIndex = indices.indexOf(co);
      const shuffledOptionsFr = (q.optionsFr && q.optionsFr.length === q.options.length)
        ? indices.map(i => q.optionsFr![i])
        : null;
      return { ...q, options: shuffledOptions, optionsFr: shuffledOptionsFr, correctOptionIndex: newCorrectIndex, originalIndices: indices };
    });
  }, [questions, language, plan]);

  // Queue-based question flow — indices into shuffledQuestions
  const queueInitialized = useRef(false);
  const [queue, setQueue] = useState<number[]>([]);
  const [queuePos, setQueuePos] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [answeredQuestionIds, setAnsweredQuestionIds] = useState<Set<number>>(new Set());
  const [skippedIds, setSkippedIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (queueInitialized.current || shuffledQuestions.length === 0 || priorAnswers === undefined) return;
    queueInitialized.current = true;

    const answeredIds = new Set((priorAnswers ?? []).map(a => a.questionId));
    const correctCount = (priorAnswers ?? []).filter(a => a.isCorrect).length;
    setAnsweredQuestionIds(answeredIds);
    setAnsweredCount(answeredIds.size);
    setRunningCorrect(correctCount);

    const unansweredQueue = shuffledQuestions
      .map((_, i) => i)
      .filter(i => !answeredIds.has(shuffledQuestions[i].id));
    setQueue(unansweredQueue);
    setQueuePos(0);
  }, [shuffledQuestions, priorAnswers]);

  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [runningCorrect, setRunningCorrect] = useState(0);

  // Bookmarks — persisted to localStorage
  const [bookmarked, setBookmarked] = useState<Set<number>>(loadBookmarks);

  // Flag modal state
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagNote, setFlagNote] = useState("");
  const [flagging, setFlagging] = useState(false);
  const [flaggedIds, setFlaggedIds] = useState<Set<number>>(new Set());

  const toggleBookmark = (questionId: number) => {
    setBookmarked(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      saveBookmarks(next);
      return next;
    });
  };

  const submitFlag = async (questionId: number) => {
    const wordCount = flagNote.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < 5) {
      toast({ title: s.flagMinWords, variant: "destructive" });
      return;
    }
    setFlagging(true);
    try {
      await fetchWithAuth(`/api/questions/${questionId}/flag`, {
        method: "POST",
        body: JSON.stringify({ note: flagNote.trim() }),
      });
      setFlaggedIds(prev => new Set([...prev, questionId]));
      setFlagOpen(false);
      setFlagNote("");
      toast({ title: s.flagSuccess, description: s.flagSuccessDesc });
    } catch (err: unknown) {
      toast({ title: s.flagError, description: err instanceof Error ? err.message : undefined, variant: "destructive" });
    } finally {
      setFlagging(false);
    }
  };

  const isExamMode = session?.mode === "exam";

  const currentShuffledIndex = queue[queuePos];
  const currentQuestion = shuffledQuestions[currentShuffledIndex];
  const totalUnique = shuffledQuestions.length;
  const pendingSkipped = skippedIds.size;
  const isQueueReady = queue.length > 0 && currentQuestion != null;
  const isLastInQueue = queuePos + 1 >= queue.length;
  const progress = totalUnique > 0 ? Math.min(100, Math.round((answeredCount / totalUnique) * 100)) : 0;

  const handleSelect = (index: number) => {
    if (isRevealed) return;
    setSelectedOption(index);
  };

  const handleSkip = () => {
    if (isRevealed || !currentQuestion) return;
    // Push this question's index to the end of the queue
    setSkippedIds(prev => new Set([...prev, currentQuestion.id]));
    setQueue(prev => [...prev, prev[queuePos]]);
    setQueuePos(prev => prev + 1);
    setSelectedOption(null);
    setLastAnswerCorrect(null);
  };

  const handleConfirm = () => {
    if (selectedOption === null || isRevealed || !currentQuestion) return;
    const originalSelectedIndex = currentQuestion.originalIndices[selectedOption];
    submitAnswer.mutate({
      data: {
        sessionId,
        questionId: currentQuestion.id,
        selectedOptionIndex: originalSelectedIndex,
      }
    }, {
      onSuccess: (result) => {
        // Remove from skipped set now that it's answered
        setSkippedIds(prev => {
          const next = new Set(prev);
          next.delete(currentQuestion.id);
          return next;
        });
        setAnsweredQuestionIds(prev => new Set([...prev, currentQuestion.id]));
        if (result.isCorrect) setRunningCorrect(prev => prev + 1);

        if (isExamMode) {
          // Exam mode: no feedback, immediately advance
          setAnsweredCount(prev => prev + 1);
          setSelectedOption(null);
          setLastAnswerCorrect(null);
          if (isLastInQueue) {
            updateSession.mutate({
              id: sessionId,
              data: { completedAt: new Date().toISOString() }
            }, {
              onSuccess: () => {
                clearActiveSession();
                queryClient.invalidateQueries({ queryKey: getGetSessionQueryKey(sessionId) });
                setLocation(`/review/${sessionId}`);
              }
            });
          } else {
            setQueuePos(prev => prev + 1);
          }
        } else {
          setIsRevealed(true);
          setLastAnswerCorrect(result.isCorrect);
        }
      }
    });
  };

  const handleNext = () => {
    setIsRevealed(false);
    setSelectedOption(null);
    setLastAnswerCorrect(null);
    setAnsweredCount(prev => prev + 1);

    if (isLastInQueue) {
      updateSession.mutate({
        id: sessionId,
        data: { completedAt: new Date().toISOString() }
      }, {
        onSuccess: () => {
          clearActiveSession();
          queryClient.invalidateQueries({ queryKey: getGetSessionQueryKey(sessionId) });
          setLocation(`/review/${sessionId}`);
        }
      });
    } else {
      setQueuePos(prev => prev + 1);
    }
  };

  function clearActiveSession() {
    localStorage.removeItem("cpl_active_session");
  }

  const handleAbort = () => {
    updateSession.mutate({
      id: sessionId,
      data: {
        completedAt: new Date().toISOString(),
        totalQuestions: answeredCount,
        correctCount: runningCorrect,
      }
    }, {
      onSuccess: () => {
        clearActiveSession();
        queryClient.invalidateQueries({ queryKey: getGetSessionQueryKey(sessionId) });
        setLocation(`/review/${sessionId}`);
      }
    });
  };

  if (sessionLoading || questionsLoading || answersLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary animate-pulse font-mono tracking-widest text-sm">SPINNING UP ENGINES...</div>
      </div>
    );
  }

  if (!session) {
    return <div className="p-8 text-center text-destructive font-mono">ERR: SESSION NOT FOUND</div>;
  }

  if (!questions || shuffledQuestions.length === 0) {
    return (
      <div className="p-8 text-center space-y-4">
        <div className="text-destructive font-mono">{s.noQuestions}</div>
        <Button onClick={() => setLocation("/practice")}>{s.backToBriefing}</Button>
      </div>
    );
  }

  if (testBlocked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-8 bg-background text-foreground">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 border border-destructive/30">
          <span className="text-2xl">✈</span>
        </div>
        <div className="text-center max-w-sm space-y-2">
          <h2 className="text-lg font-bold font-mono tracking-tight text-destructive">SESSION CONFLICT</h2>
          <p className="text-sm text-muted-foreground font-mono">
            {testBlockMessage ||
              "A test is already in progress in another tab, window, or device. Close it or finish that session first."}
          </p>
        </div>
        <Button variant="outline" onClick={() => setLocation("/practice")}>Back to Briefing</Button>
      </div>
    );
  }

  if (queueInitialized.current && queue.length === 0) {
    if (!session.completedAt) {
      updateSession.mutate({
        id: sessionId,
        data: { completedAt: new Date().toISOString() }
      }, {
        onSuccess: () => {
          clearActiveSession();
          queryClient.invalidateQueries({ queryKey: getGetSessionQueryKey(sessionId) });
          setLocation(`/review/${sessionId}`);
        }
      });
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary animate-pulse font-mono tracking-widest text-sm">{s.filingFlightPlan}</div>
      </div>
    );
  }

  // Queue not yet initialized (brief render before useEffect fires)
  if (!isQueueReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary animate-pulse font-mono tracking-widest text-sm">{s.filingFlightPlan}</div>
      </div>
    );
  }

  const isBookmarked = bookmarked.has(currentQuestion.id);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border bg-card p-4 shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {isExamMode ? (
              <GraduationCap className="h-5 w-5 text-yellow-400" />
            ) : (
              <PlaneTakeoff className="h-5 w-5 text-primary" />
            )}
            <div className={cn("font-mono text-sm tracking-widest", isExamMode ? "text-yellow-400" : "text-primary")}>
              {isExamMode ? s.examTitle : `FLIGHT ${sessionId}`}
            </div>
            {isExamMode && (
              <span className="text-xs font-mono bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-full">
                {s.noFeedback}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-sm font-mono text-muted-foreground">
              {answeredCount} / {totalUnique} {s.waypoints}
            </div>
            {!isExamMode && pendingSkipped > 0 && (
              <span className="text-xs font-mono bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-full">
                {pendingSkipped} {s.skippedCount}
              </span>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive font-mono text-xs tracking-wider"
                >
                  <LogOut className="w-3.5 h-3.5 mr-1.5" />
                  {s.abort}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-card border-border">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-mono tracking-wide">{s.abortTitle}</AlertDialogTitle>
                  <AlertDialogDescription className="text-muted-foreground">
                    {answeredCount > 0
                      ? s.abortBodyAnswered.replace("{n}", String(answeredCount)).replace("{total}", String(totalUnique))
                      : s.abortBodyNone}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="font-mono text-xs">{s.continueSession}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleAbort}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90 font-mono text-xs"
                  >
                    {s.confirmAbort}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        <div className="max-w-4xl mx-auto mt-4">
          <Progress value={progress} className="h-1" indicatorClassName="bg-primary transition-all" />
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-4xl mx-auto space-y-8 study-layout study-layout--session animate-in fade-in slide-in-from-bottom-4 duration-500">

          {/* Category + Question ID + Bookmark */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-mono text-muted-foreground tracking-wider uppercase">
                {currentQuestion.category}
              </span>
              <span className="text-xs font-mono text-muted-foreground/40">
                Q#{currentQuestion.id}
              </span>
              {skippedIds.has(currentQuestion.id) && (
                <span className="text-xs font-mono bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-0.5 rounded-full">
                  {s.skippedBadge}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => toggleBookmark(currentQuestion.id)}
                title={isBookmarked ? "Remove bookmark" : "Save for later"}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-mono transition-colors border",
                  isBookmarked
                    ? "bg-primary/20 text-primary border-primary/40 hover:bg-primary/10"
                    : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/30"
                )}
              >
                {isBookmarked
                  ? <><BookmarkCheck className="w-3.5 h-3.5" /> {s.saved}</>
                  : <><Bookmark className="w-3.5 h-3.5" /> {s.save}</>
                }
              </button>
              <button
                onClick={() => { setFlagOpen(true); setFlagNote(""); }}
                title={flaggedIds.has(currentQuestion.id) ? s.flagAlready : s.flagQuestion}
                disabled={flaggedIds.has(currentQuestion.id)}
                className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-mono transition-colors border",
                  flaggedIds.has(currentQuestion.id)
                    ? "bg-orange-500/20 text-orange-400 border-orange-500/40 opacity-70 cursor-default"
                    : "bg-transparent text-muted-foreground border-border hover:text-orange-400 hover:border-orange-400/40"
                )}
              >
                <Flag className="w-3.5 h-3.5" /> {s.flagQuestion}
              </button>
            </div>
          </div>

          <h2 className="study-question-text text-2xl md:text-3xl font-bold leading-relaxed text-foreground">
            {language === "fr" ? (currentQuestion.textFr ?? currentQuestion.text) : currentQuestion.text}
          </h2>

          {currentQuestion.imageUrl && (
            <ZoomableImage
              src={currentQuestion.imageUrl}
              alt="Question diagram"
              thumbnailClass="max-h-72 md:max-h-96"
            />
          )}

          <div className="study-options-grid grid grid-cols-1 gap-3">
            {currentQuestion.options.map((opt, i) => {
              const isSelected = selectedOption === i;
              const isCorrect = i === Number(currentQuestion.correctOptionIndex);

              let stateClass = "border-border bg-card hover:bg-secondary text-foreground";
              let Icon = null;

              if (isRevealed) {
                if (isCorrect) {
                  stateClass = "border-primary bg-primary/10 text-primary";
                  Icon = CheckCircle2;
                } else if (isSelected && !isCorrect) {
                  stateClass = "border-destructive bg-destructive/10 text-destructive";
                  Icon = XCircle;
                } else {
                  stateClass = "border-border/50 bg-card/50 text-muted-foreground opacity-50";
                }
              } else if (isSelected) {
                stateClass = "border-primary bg-primary/20 text-primary shadow-[0_0_15px_rgba(var(--primary),0.2)]";
              }

              return (
                <button
                  key={i}
                  onClick={() => handleSelect(i)}
                  disabled={isRevealed}
                  className={cn(
                    "study-answer-btn flex items-center gap-4 p-5 rounded-lg border-2 text-left transition-all duration-300",
                    stateClass
                  )}
                >
                  <div className={cn(
                    "study-answer-letter flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-bold font-mono transition-colors shrink-0",
                    isRevealed && isCorrect ? "border-primary text-primary" :
                    isRevealed && isSelected && !isCorrect ? "border-destructive text-destructive" :
                    isSelected ? "border-primary text-primary bg-primary/10" : "border-muted-foreground text-muted-foreground"
                  )}>
                    {String.fromCharCode(65 + i)}
                  </div>
                  <span className="study-answer-label flex-1 text-lg">
                    {language === "fr" ? (currentQuestion.optionsFr?.[i] ?? opt) : opt}
                  </span>
                  {Icon && <Icon className="w-6 h-6 animate-in zoom-in duration-300 shrink-0" />}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 flex-wrap">
            {!isRevealed ? (
              <>
                {!isExamMode && (
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={handleSkip}
                    disabled={submitAnswer.isPending}
                    className="font-mono border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10 hover:text-yellow-300"
                  >
                    <SkipForward className="w-4 h-4 mr-2" />
                    {s.skipAnswerLater}
                  </Button>
                )}
                <Button
                  size="lg"
                  onClick={handleConfirm}
                  disabled={selectedOption === null || submitAnswer.isPending || updateSession.isPending}
                  className={cn(
                    "font-bold tracking-wide flex-1 md:flex-none",
                    isExamMode && "bg-yellow-500 hover:bg-yellow-400 text-black"
                  )}
                >
                  {(submitAnswer.isPending || updateSession.isPending)
                    ? s.submitting
                    : isExamMode
                      ? isLastInQueue ? s.submitFinishExam : s.submitAnswer
                      : s.confirmAnswer}
                </Button>
              </>
            ) : (
              <Button
                size="lg"
                onClick={handleNext}
                disabled={updateSession.isPending}
                className="w-full md:w-auto font-bold tracking-wide animate-in fade-in ml-auto"
              >
                {isLastInQueue ? s.completeSession : s.nextWaypoint}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>

          {isRevealed && (
            <Card className={cn(
              "mt-8 border bg-card/50 animate-in slide-in-from-top-4 duration-500",
              lastAnswerCorrect ? "border-primary/50" : "border-destructive/50"
            )}>
              <CardContent className="p-6">
                <h3 className={cn(
                  "text-lg font-bold mb-2 flex items-center gap-2",
                  lastAnswerCorrect ? "text-primary" : "text-destructive"
                )}>
                  {lastAnswerCorrect ? (
                    <><CheckCircle className="w-5 h-5" /> {s.affirmative}</>
                  ) : (
                    <><XCircle className="w-5 h-5" /> {s.negative}</>
                  )}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {language === "fr" ? (currentQuestion.explanationFr ?? currentQuestion.explanation) : currentQuestion.explanation}
                </p>
                {currentQuestion.explanationImageUrl && (
                  <div className="mt-4">
                    <ZoomableImage
                      src={currentQuestion.explanationImageUrl}
                      alt="Explanation diagram"
                      thumbnailClass="max-h-64"
                    />
                  </div>
                )}
                {currentQuestion.referenceUrl && (
                  <div className="mt-4">
                    <a
                      href={currentQuestion.referenceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full rounded-lg bg-blue-500/10 border border-blue-500/30 px-4 py-3 text-sm font-semibold text-blue-300 hover:bg-blue-500/20 hover:text-blue-200 transition-colors active:scale-95"
                    >
                      <span>📋</span>
                      <span>View Official Reference</span>
                      <span className="ml-auto text-xs font-normal opacity-70">↗</span>
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

        </div>
      </main>

      {/* Flag Question Modal */}
      {flagOpen && currentQuestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => !flagging && setFlagOpen(false)} />
          <div className="relative z-10 bg-card border border-orange-500/40 rounded-lg p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-orange-500/20 p-2 rounded-md shrink-0">
                <Flag className="w-5 h-5 text-orange-400" />
              </div>
              <div className="min-w-0">
                <h2 className="font-bold text-foreground font-mono tracking-wide text-sm">{s.flagTitle}</h2>
                <p className="text-[10px] font-mono text-muted-foreground/60">Q#{currentQuestion.id}</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">{s.flagDesc}</p>
            <Textarea
              value={flagNote}
              onChange={e => setFlagNote(e.target.value)}
              placeholder={s.flagNotePlaceholder}
              className="mb-4 resize-none h-24 text-sm bg-background"
              disabled={flagging}
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setFlagOpen(false)}
                disabled={flagging}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                disabled={flagging}
                onClick={() => submitFlag(currentQuestion.id)}
              >
                <Flag className="w-3.5 h-3.5 mr-1.5" />
                {flagging ? s.flagSubmitting : s.flagSubmit}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
